import { Captura, Dot, Section } from "./comunes";

export default function AyudaPanelConfig() {
  return (
    <>
      <Section id="panel" title="Panel y estadísticas">
        <Captura
          src="/ayuda-img/panel.png"
          alt="Panel principal"
          caption="El Panel: contadores operativos, casos activos con semáforos y filtros. Las estadísticas viven en su propia vista."
        />
        <p>
          En la vista <strong>Estadísticas</strong> (en el menú, entre Panel
          y Casos) vive el panorama del histórico:
          qué <strong>marcas, modelos y empresas</strong> generan más soporte
          (con cuántos terminaron en garantía), los <strong>casos por
          categoría</strong> (qué tipo de soporte piden los clientes, con un
          bucket "sin clasificar"), las <strong>fallas recurrentes por
          modelo</strong> y la <strong>antigüedad de los
          casos abiertos</strong> (promedio, distribución y los más viejos con
          link directo). <strong>Toca cualquier renglón</strong> de las
          estadísticas para saltar a la lista de casos ya filtrada por esa
          marca, modelo, empresa, categoría o falla. Arriba verás{" "}
          <strong>¿Dónde está la pelota?</strong>: de los casos abiertos,
          cuántos y cuánto tiempo llevan <strong>con nosotros</strong>,
          <strong>esperando al cliente</strong> o <strong>con el
          proveedor</strong>.
        </p>
        <p className="mt-2">
          <strong>Resumen ejecutivo</strong> (admin y soporte): una pestaña
          con KPIs del periodo para dirección — casos creados/cerrados con
          tendencia vs el periodo anterior, tiempo de resolución (incluido el
          <strong>real desde que el cliente reportó</strong>), la
          <strong>salud del backlog</strong> (días para vaciarlo al ritmo
          actual) y distribuciones — descargable en <strong>PDF</strong> con
          membrete
          Emprenet.
        </p>
        <Captura
          src="/ayuda-img/estadisticas-movil.png"
          alt="Estadísticas en el celular"
          caption="Las estadísticas también viven en el celular: antigüedad, distribución y los casos más viejos."
        />
        <p className="mt-2 text-zinc-400">
          Dos medidas de tiempo distintas: la <strong>antigüedad</strong>{" "}
          cuenta desde que se creó el caso; el semáforo de{" "}
          <strong>actividad</strong> ("hace N días") cuenta desde el último
          movimiento. Un caso puede ser viejo pero estar al día.
        </p>
      </Section>

      <Section id="escalacion" title="Inactividad y escalación automática">
        <p>
          Un proceso revisa los casos abiertos periódicamente (por defecto
          cada 30 minutos, configurable) con los mismos umbrales del semáforo:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li><Dot color="emerald" /> <strong>Verde</strong>: con movimiento reciente.</li>
          <li><Dot color="amber" /> <strong>Amarillo — por quemarse</strong>: avisa al asignado.</li>
          <li><Dot color="red" /> <strong>Rojo — quemado</strong>: requiere acción inmediata.</li>
          <li><Dot color="red" /> <strong>Escalación</strong>: además se notifica a los administradores.</li>
        </ul>
      </Section>

      <Section id="configuracion" title="Configuración (solo admin)">
        <Captura
          src="/ayuda-img/config-menu.png"
          alt="Configuración con su menú lateral"
          caption="El menú de la izquierda agrupa las secciones por lo que son. Cada sección se lee y se guarda sola."
        />
        <p>
          Las secciones están agrupadas en cinco bloques:{" "}
          <strong>Catálogos</strong> (lo que el equipo mantiene: marcas, modelos,
          categorías y los tres tipos), <strong>Directorio</strong> (clientes y
          contactos, proveedores, asesores comerciales),{" "}
          <strong>Automatizaciones</strong> (cuándo actúa el sistema solo),{" "}
          <strong>Integraciones</strong> (Jimi, Queclink, Telegram) y{" "}
          <strong>Correo y empresa</strong>.
        </p>
        <p className="mt-2">
          <strong>Cómo se guarda</strong>: un <strong>switch</strong> se aplica al
          momento, con su aviso. Lo que se escribe —horas, correos, contraseñas,
          umbrales— se guarda con el <strong>botón de su sección</strong>, y cada
          sección guarda <strong>sólo lo suyo</strong>: cambiar los buzones del
          correo no toca el nombre de la empresa ni las horas de los avisos.
        </p>
        <Captura
          src="/ayuda-img/config-movil.png"
          alt="Configuración en el celular"
          caption="En el celular primero se ve la lista de secciones; al tocar una entras a ella y regresas con «‹ Configuración»."
        />
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            <strong>Tiempos y escalación</strong> (Automatizaciones): los umbrales
            de inactividad (amarillo &lt; rojo ≤ escalación) y, opcionalmente, un{" "}
            <strong>SLA por estado</strong> con umbrales distintos según el estado
            del caso. Un campo vacío usa el general, y el texto gris del campo te
            dice cuál se aplicaría.
          </li>
          <li>
            <strong>Avisos programados</strong>: el resumen diario de casos sin
            seguimiento y el aviso de casos <strong>esperando al cliente</strong>{" "}
            (interno, en días hábiles, saltando los feriados mexicanos). Al cliente
            no se le manda nada.
          </li>
          <li>
            <strong>Correo entrante</strong>: sólo entra a la Bandeja el correo
            dirigido a los buzones de la lista (hoy <em>casos@arknex.net</em>). El
            spam de diccionario a <em>info@</em>, <em>admin@</em> y compañía se
            ignora solo y queda en la auditoría. Vacío = entra todo.
          </li>
          <li>
            <strong>Catálogos</strong>: renombrar aquí <strong>no</strong> renombra
            los casos viejos, porque cada caso guarda el texto con el que se creó.
            Para sacar algo de la lista sin perder el histórico, desactívalo en vez
            de borrarlo; una categoría con casos no se deja eliminar. Al crear un
            modelo, si ya existe uno parecido —mismo nombre con otro formato, un
            typo o una variante— sale un <strong>aviso con los similares</strong> y
            quién los agregó: puedes usar el que ya está con un clic o crear el
            tuyo de todos modos.
            <Captura
              src="/ayuda-img/catalogo-similares.png"
              alt="Aviso de modelos similares al crear un modelo"
              caption="El aviso al crear un modelo: los parecidos con quién los agregó, «Usar este» y «Crear de todos modos»."
            />
          </li>
          <li>
            <strong>Llave de Claude</strong> (Integraciones): la llave con la que la app le habla a
            la IA. De ella dependen la revisión de la bandeja, el resumen ejecutivo, los comentarios
            de correo, el análisis de la sala de cierre y la traducción del chino de los tickets de
            Jimi. Se puede <strong>rotar desde ahí</strong>: se comprueba con Anthropic antes de
            guardarla —si no funciona, no se guarda y la de antes se queda—, se guarda cifrada y
            aplica al momento sin reiniciar nada. El botón <strong>Probar</strong> no gasta nada.
          </li>
          <li>
            <strong>Mensajes rápidos</strong> (Correo y empresa): las frases de un
            clic del recuadro «Pasar a un compañero». Antes esta sección se llamaba
            «Notificaciones», que no decía lo que era.
          </li>
          <li>
            <strong>Roles</strong>: admin (todo), soporte (opera casos), usuario
            (sólo lectura). Usuarios y contraseñas se administran en /admin.
          </li>
        </ul>
      </Section>

      <Section id="atajos" title="Atajos de teclado">
        <ul className="space-y-1.5 text-sm">
          <li><code className="text-accent-300">Ctrl + /</code> — abrir el menú de ayuda</li>
          <li><code className="text-accent-300">Esc</code> — cerrar ventana o menú emergente</li>
          <li><code className="text-accent-300">/</code> — enfocar el buscador (en Casos)</li>
          <li><code className="text-accent-300">J / K</code> — moverse entre filas (en Casos)</li>
          <li><code className="text-accent-300">Enter</code> — abrir el caso resaltado</li>
        </ul>
      </Section>
    </>
  );
}
